import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Card, 
  Form, 
  Button, 
  Row, 
  Col, 
  Dropdown, 
  Spinner, 
  Alert
} from 'react-bootstrap';
import ForecastResults from './ForecastResults';

const ForecastMode = ({ 
  models, 
  selectedModel, 
  onModelSelect, 
  apiBaseUrl,
  selectedFireworkModel,
  setSelectedFireworkModel
}) => {
  const [previousExams, setPreviousExams] = useState([]);
  const [subject, setSubject] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [forecast, setForecast] = useState(null);
  const [fireworkModels, setFireworkModels] = useState([]);
  

  // Fetch Firework models when models are loaded
  useEffect(() => {
    const fetchFireworkModels = async () => {
      try {
        const response = await axios.get(`${apiBaseUrl}/models`);
        if (response.data.firework_models) {
          const fireworkModelsList = Object.entries(response.data.firework_models).map(([id, name]) => ({
            id,
            name
          }));
          setFireworkModels(fireworkModelsList);
        }
      } catch (err) {
        console.error('Error fetching Firework models:', err);
      }
    };
    
    if (models.length > 0) {
      fetchFireworkModels();
    }
  }, [models, apiBaseUrl]);

  const handlePreviousExamsChange = (e) => {
    setPreviousExams(Array.from(e.target.files));
  };

  const handleSubjectChange = (e) => {
    setSubject(e.target.value);
  };

  const handleFireworkModelSelect = (modelId) => {
    if (setSelectedFireworkModel) {
      setSelectedFireworkModel(modelId);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (previousExams.length === 0) {
      setError('Please upload at least one previous exam paper');
      return;
    }
    
    if (!subject.trim()) {
      setError('Please enter the subject or course name');
      return;
    }
    
    if (!selectedModel) {
      setError('Please select an AI model');
      return;
    }
    
    setLoading(true);
    setError('');
    setForecast(null);
    
    const formData = new FormData();
    previousExams.forEach(file => {
      formData.append('previous_exams', file);
    });
    formData.append('ai_provider', selectedModel);
    formData.append('subject', subject);
    
    // Add firework_model parameter if Firework is selected
    if (selectedModel === 'firework' && selectedFireworkModel) {
      formData.append('firework_model', selectedFireworkModel);
    }
    
    try {
      // First check if backend is reachable
      try {
        await axios.get(`${apiBaseUrl}/health`);
      } catch (healthErr) {
        throw new Error(`Backend server unreachable. Please check if the backend service is running: ${healthErr.message}`);
      }
      
      // Calculate timeout based on file sizes and count
      const totalSizeMB = previousExams.reduce((total, file) => total + file.size, 0) / (1024 * 1024);
      const timeout = 600000;
      
      // Show processing message for large files
      if (totalSizeMB > 5 || previousExams.length > 3) {
        setError(`Processing ${Math.round(totalSizeMB)}MB of exam papers, this may take several minutes. Please wait...`);
      }
      
      const response = await axios.post(`${apiBaseUrl}/forecast-exam`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        timeout: timeout
      });
      
      if (response.data && response.data.forecast) {
        setForecast(response.data);
        setError(''); // Clear any processing messages
      } else {
        throw new Error('Invalid response format from server');
      }
    } catch (err) {
      console.error('Error submitting form:', err);
      
      // Handle different error scenarios with useful messages
      if (err.response) {
        // Server responded with an error status
        const serverMessage = err.response.data?.detail || JSON.stringify(err.response.data) || err.message;
        setError(`Server error (${err.response.status}): ${serverMessage}`);
      } else if (err.request) {
        // Request was made but no response received (timeout)
        if (err.code === 'ECONNABORTED') {
          setError(`Request timed out. Processing ${previousExams.length} files takes longer than expected. Try with fewer or smaller files.`);
        } else {
          setError('No response from server. Please check if the backend service is running.');
        }
      } else {
        // Error in request setup
        setError(`Error: ${err.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  // Render forecast results if available
  if (forecast) {
    return (
      <div>
        <Button 
          variant="outline-secondary" 
          onClick={() => setForecast(null)} 
          className="mb-3"
        >
          ← Back to Forecast Form
        </Button>
        <ForecastResults forecast={forecast} subject={subject} />
      </div>
    );
  }

  // Render the previous exams upload form
  return (
    <Card>
      <Card.Body>
        <Card.Title className="mb-4">Forecast Exam Questions</Card.Title>
        
        {error && <Alert variant={error.includes('Processing') ? "info" : "danger"}>{error}</Alert>}
        
        <Form onSubmit={handleSubmit}>
          <Row className="mb-3">
            <Col md={12}>
              <Form.Group controlId="previousExams">
                <Form.Label>Upload Previous Exam Papers</Form.Label>
                <Form.Control 
                  type="file" 
                  multiple 
                  onChange={handlePreviousExamsChange} 
                  accept=".pdf,.docx,.doc,.txt"
                />
                <Form.Text className="text-muted">
                  Upload previous year exam papers or question banks (PDF, DOCX, DOC, or TXT)
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>
          
          <Row className="mb-3">
            <Col md={6}>
              <Form.Group controlId="subject">
                <Form.Label>Subject / Course Name</Form.Label>
                <Form.Control 
                  type="text" 
                  value={subject}
                  onChange={handleSubjectChange}
                  placeholder="e.g., Computer Science, Organic Chemistry, etc."
                />
              </Form.Group>
            </Col>
            
            <Col md={6}>
              <Form.Group controlId="aiModel">
                <Form.Label>Select AI Model</Form.Label>
                <Dropdown>
                  <Dropdown.Toggle variant="outline-primary" id="dropdown-model" className="w-100 text-start">
                    {models.find(m => m.id === selectedModel)?.name || 'Select AI Model'}
                  </Dropdown.Toggle>
                  <Dropdown.Menu className="w-100">
                    {models.map(model => (
                      <Dropdown.Item 
                        key={model.id} 
                        onClick={() => onModelSelect(model.id)}
                        active={selectedModel === model.id}
                      >
                        {model.name}
                      </Dropdown.Item>
                    ))}
                  </Dropdown.Menu>
                </Dropdown>
              </Form.Group>
            </Col>
          </Row>
          
          {/* Show Firework model selection only when Firework is selected */}
          {selectedModel === 'firework' && fireworkModels.length > 0 && (
            <Row className="mb-3">
              <Col md={12}>
                <Form.Group controlId="fireworkModel">
                  <Form.Label>Select OpenSource AI Model</Form.Label>
                  <Dropdown>
                    <Dropdown.Toggle variant="outline-secondary" id="dropdown-firework-model" className="w-100 text-start">
                      {fireworkModels.find(m => m.id === selectedFireworkModel)?.name || 'Select AI Model'}
                    </Dropdown.Toggle>
                    <Dropdown.Menu className="w-100">
                      {fireworkModels.map(model => (
                        <Dropdown.Item 
                          key={model.id} 
                          onClick={() => handleFireworkModelSelect(model.id)}
                          active={selectedFireworkModel === model.id}
                        >
                          {model.name}
                        </Dropdown.Item>
                      ))}
                    </Dropdown.Menu>
                  </Dropdown>
                  <Form.Text className="text-muted">
                    Select a specific AI model for exam forecasting
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
          )}
          
          <div className="d-grid gap-2">
            <Button 
              variant="primary" 
              type="submit" 
              disabled={loading || previousExams.length === 0 || !subject.trim()}
              size="lg"
            >
              {loading ? <><Spinner animation="border" size="sm" /> Analyzing Exams...</> : 'Forecast Questions'}
            </Button>
          </div>
        </Form>
      </Card.Body>
    </Card>
  );
};

export default ForecastMode;